import React, { useMemo, useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Box, Flex, useToast, Text, Button } from '@chakra-ui/react'
import {
  useSessionControllerGetNotes,
  useSessionControllerTrackEvent,
  useSessionControllerGetProgressNotesForSession
} from '@core/api'
import { copyToClipboard } from '@utilities'
import { useQuery } from 'react-query'
import { endpoints } from '@api'
import ContentCopyIcon from '@material-ui/icons/FileCopy'

import {
  ProgressNoteGenerationSteps,
  TranscriptionError,
  ProgressNote
} from '../sessions/components'
import * as clinicianTracking from '../../lib/clinician-tracking'
import { NavBar, ExtensionWrapper } from './'

import _ from 'lodash'

export function ExtensionSessionNotes() {
  const {
    sessionId,
    id: clientId
  }: { sessionId: string; id: string } = useParams()
  const [selectedProgressNoteId, setSelectedProgressNoteId] = useState('')
  const toast = useToast()

  const { isLoading: isSessionLoading } = useSessionControllerGetNotes(
    sessionId
  )

  const {
    data: progressNoteData,
    isLoading: isProgressNotesLoading
  } = useSessionControllerGetProgressNotesForSession(sessionId, {
    query: {
      refetchInterval: (data: any) => {
        if (data?.sessionProgressNotes.some((pn: any) => pn.note?.isLoading)) {
          return 5000
        }

        return false
      }
    }
  })

  const isAnyNoteLoading = useMemo(
    () =>
      progressNoteData?.sessionProgressNotes?.some(
        (pn: any) => pn.note?.isLoading
      ),
    [progressNoteData?.sessionProgressNotes]
  )

  useEffect(() => {
    if (isAnyNoteLoading) {
      const loadingNote = progressNoteData?.sessionProgressNotes?.find(
        (pn: any) => pn.note?.isLoading
      )
      setSelectedProgressNoteId(loadingNote?.id)
    }
  }, [isAnyNoteLoading, progressNoteData?.sessionProgressNotes])

  const progressNote = useMemo(
    () =>
      progressNoteData?.sessionProgressNotes?.find(
        (pn: any) => pn.id === selectedProgressNoteId
      ) || progressNoteData?.sessionProgressNotes[0],
    [progressNoteData, selectedProgressNoteId]
  )

  const { mutate: trackEvent } = useSessionControllerTrackEvent()

  const { isLoading: isClientLoading }: any = useQuery(
    [endpoints.getClinicianUserAccount.getCacheId(), clientId],
    () => endpoints.getClinicianUserAccount.request({ id: clientId })
  )

  if (
    isProgressNotesLoading ||
    isSessionLoading ||
    _.isEmpty(progressNote) ||
    isClientLoading
  )
    return null

  const noteType = progressNote?.noteType || 'soap'

  const handleTrackEvent = (event: string, payload: object) => {
    trackEvent({ id: sessionId, data: { eventType: event, payload } })
  }

  const handleCopyAllToClipboard = ({ isNext = false }) => {
    let values: string[] = []

    if (isNext) {
      progressNote?.template?.sections.forEach(
        ({ key, displayName }: { key: string; displayName: string }) => {
          if (progressNote?.nextNote[key]) {
            values.push(`${displayName}\n${progressNote?.nextNote[key]}`)
          }
        }
      )
    } else {
      progressNote?.template?.sections.forEach(
        ({ key, displayName }: { key: string; displayName: string }) => {
          if (progressNote?.note[key]) {
            values.push(`${displayName}\n${progressNote?.note[key]}`)
          }
        }
      )
    }

    const value = values.join('\n\n')

    copyToClipboard(value)
    clinicianTracking.trackEvent('Copied Progress Note', {
      noteType,
      noteId: progressNote?.id
    })
    handleTrackEvent('copy_all_to_clipboard_clicked', {
      noteType
    })
    toast({
      title: 'Copied to clipboard!',
      status: 'success',
      duration: 1500
    })
  }

  const transcriptionError = progressNote?.generationStepStatuses?.some(
    ({ step, status }: any) =>
      step === 'TRANSCRIBING_AUDIO' && status === 'ERROR'
  )

  return (
    <ExtensionWrapper>
      <NavBar showBackButton />
      <Flex direction="column" mt="0px !important">
        {isAnyNoteLoading ? (
          <Box m="small">
            <ProgressNoteGenerationSteps
              progressNoteGenerationStepStatuses={
                progressNoteData?.generationStepStatuses || []
              }
            />
          </Box>
        ) : (
          <Box>
            {transcriptionError && <TranscriptionError isExtension />}
            <Box w="100%" h="100%" backgroundColor="white" p="small">
              <Flex justifyContent="space-between" alignItems="center" mb="small">
                <Text>
                  Note Type:{' '}
                  <Text as="b">{progressNote.template.displayName}</Text>
                </Text>
                <Button
                  id={`pendo-copy-all-${noteType}-notes-tag`}
                  borderRadius="24px"
                  onClick={() => handleCopyAllToClipboard({ isNext: false })}
                  m={0}
                >
                  <ContentCopyIcon style={{ fontSize: 16, marginRight: 8 }} />
                  Copy all
                </Button>
              </Flex>
              <ProgressNote progressNote={progressNote} />
            </Box>
          </Box>
        )}
      </Flex>
    </ExtensionWrapper>
  )
}
